import { Organization, People, World } from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import {
  ChartWidgetType,
  MouseMoveEvent
} from "@/components/graphs/WinRate/BarChart";
import { ChartIcons } from "@/components/graphs/WinRate/ChartIcons";
import { LineAndBarChartCustomTooltip } from "@/components/graphs/WinRate/LineAndBarChartCustomTooltip";

import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { ChartDataType } from "@/entities/reporting/types";
import { UserData } from "@/entities/users/types";
import { isEmpty } from "lodash";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  TooltipProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";

interface Props {
  height: number;
  chartData: ChartDataType[];
  showComparison?: boolean;
  userData?: UserData;
  chartWidgetType?: ChartWidgetType;
  hideIndustry?: boolean;
}

const LineChart: React.FC<Props> = ({
  height,
  chartData,
  showComparison,
  userData,
  chartWidgetType = "leadAnalysis",
  hideIndustry
}) => {
  if (isEmpty(chartData)) return null;

  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [xAxisHeight, setXAxisHeight] = useState(0);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (chartRef.current) {
      const xAxisElement =
        chartRef.current.container.querySelector(".recharts-xAxis");
      if (xAxisElement) {
        const rect = xAxisElement.getBoundingClientRect();
        setXAxisHeight(rect.height);
      }
    }
  }, [chartData]);

  const icons: { [key: string]: JSX.Element } = {
    you: <UserAvatar size="M" userData={userData} />,
    team: <People />,
    organization: (
      <IconContainer size="md">
        <Organization />
      </IconContainer>
    ),
    ...(!hideIndustry && {
      industry: (
        <IconContainer size="md" nativeStroke className="grow justify-start">
          <World />
        </IconContainer>
      )
    })
  };

  const handleMouseMove = (e: MouseMoveEvent) => {
    if (e && e.activeTooltipIndex !== undefined && e.activeCoordinate) {
      setTooltipPos({
        x: e.activeCoordinate.x - 50,
        y: showComparison ? -100 : 5
      });
    }
  };

  const MemoizedComparisonTooltip = useMemo(() => {
    const ComparisonTooltip = ({
      active,
      payload
    }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload as ChartDataType;
        const tooltipData = {
          ...data,
          youValue: Math.round(data.youValue),
          teamValue: Math.round(data.teamValue),
          organizationValue: Math.round(data.organizationValue),
          ...(!hideIndustry && {
            industryValue: Math.round(data.industryValue || 0)
          })
        };
        return (
          <LineAndBarChartCustomTooltip
            data={tooltipData}
            userData={userData}
            showComparison={showComparison}
            chartWidgetType={chartWidgetType}
            hideIndustry={hideIndustry}
          />
        );
      }
      return null;
    };
    return memo(ComparisonTooltip);
  }, [userData, showComparison]);

  const chartHeight = xAxisHeight > 0 ? height - xAxisHeight : height;

  return (
    <div className="flex w-full" style={{ height: `${height}px` }}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart
          ref={chartRef}
          data={chartData}
          margin={{ left: 25, right: 25, top: 5 }}
          onMouseMove={handleMouseMove}
        >
          <CartesianGrid
            strokeDasharray="5 5"
            stroke="var(--widget)"
            horizontal={false}
          />
          <XAxis
            dataKey="date"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 10 }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 12 }}
            tickFormatter={(tick) => `${tick}%`}
          />
          {showComparison && (
            <>
              <Line
                type="linear"
                dataKey="teamValue"
                stroke="var(--widget)"
                activeDot={false}
                dot={{ fill: "var(--widget)" }}
              />
              <Line
                type="linear"
                dataKey="organizationValue"
                stroke="var(--widget)"
                activeDot={false}
                dot={{ fill: "var(--widget)" }}
              />
              {!hideIndustry && (
                <Line
                  type="linear"
                  dataKey="industryValue"
                  stroke="var(--widget)"
                  activeDot={false}
                  dot={{ fill: "var(--widget)" }}
                />
              )}
            </>
          )}
          <Line
            type="linear"
            dataKey="youValue"
            stroke="var(--cta)"
            activeDot={false}
            dot={{ fill: "var(--cta)" }}
          />
          <RechartsTooltip
            content={<MemoizedComparisonTooltip />}
            cursor={{ fill: "transparent" }}
            position={tooltipPos}
            wrapperStyle={{ visibility: tooltipPos.x ? "visible" : "hidden" }}
          />
          <Legend
            content={() => (
              <div className="absolute -bottom-3 left-1/2 w-full">
                <Text
                  as="div"
                  variant="smBodySecondary"
                >{`${chartData[0].date}-${chartData[chartData.length - 1].date}`}</Text>
              </div>
            )}
            verticalAlign="bottom"
            align="center"
          />
        </RechartsLineChart>
      </ResponsiveContainer>
      <ChartIcons
        chartData={chartData}
        height={chartHeight}
        icons={icons}
        showComparison={showComparison}
      />
    </div>
  );
};

export default LineChart;
